// Edits one Section's seat grid in memory, for the admin's seat editor (IDV-704). Every change is one undoable step;
// nothing reaches the API until the dialog saves `payload()`, all or nothing.
//
// Rows and seats are held by id, never by reference: undo swaps the draft for a fresh copy. Rows and seats created
// here get a `new-` id, which the payload leaves out so the API creates them.
import { computed, reactive, ref, type Ref } from 'vue'
import type SectionGrid from '@/types/SectionGrid'
import type { GridRow, GridSeat, SeatFields, SectionGridPayload } from '@/types/SectionGrid'
import { SeatType, SeatTypeLabels } from '@/constants/SeatTypes'
import { v4 } from 'uuid'

/** A change the draft holds over the saved grid, as shown in the pending list */
export interface Change {
  kind: 'create' | 'update' | 'remove'
  text: string
}

export type NumberingPattern = 'ascending' | 'descending' | 'centre_odd_left' | 'centre_even_left'

export interface HistoryEntry {
  label: string
  /** The draft before the step, as JSON */
  before: string
}

/** A seat with the Row it sits in */
export interface PlacedSeat {
  row: GridRow
  seat: GridSeat
}

/** A cell of the grid: a seat, or an empty column of a Row */
export interface GridCell {
  row: GridRow
  /** 0 is the front Row */
  rowIndex: number
  position: number
  seat?: GridSeat
}

export type SeatCell = GridCell & { seat: GridSeat }

/** An error message, or null when the change was made */
export type Result = string | null

const NEW = 'new-'

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T

export function isNew(id: string): boolean {
  return id.startsWith(NEW)
}

/** What the ticket prints: the Row symbol and the Label, or the Name when there is no Label ("A05 – PCD") */
export function fullLabel(row: Pick<GridRow, 'symbol'>, seat: Pick<GridSeat, 'name' | 'label'>): string {
  return `${row.symbol}${textOrNull(seat.label) ?? seat.name}`
}

/** The text trimmed, or null when nothing is left: a blank Label means the seat has none */
export function textOrNull(value: string | null | undefined): string | null {
  const text = value?.trim() ?? ''
  return text === '' ? null : text
}

export function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`
}

/** 2 when the Row's names are zero-padded ("05"), 1 when they aren't, `fallback` for an empty Row */
export function rowDigits(row: GridRow, fallback: number): number {
  if (!row.seats.length) return fallback
  return row.seats.some(seat => /^0\d/.test(seat.name)) ? 2 : 1
}

/**
 * The names a numbering pattern gives, left to right, zero-padded to `digits` ("05"). Centre-out numbering puts 1 in
 * the middle and alternates sides: odds on the left (…5, 3, 1, 2, 4…) or evens on the left (…4, 2, 1, 3, 5…).
 */
export function numberSeries(count: number, pattern: NumberingPattern, start = 1, digits = 1): string[] {
  const numbers = Array.from({ length: Math.max(0, count) }, (_, i) => start + i)
  const odds = numbers.filter((_, i) => i % 2 === 0)
  const evens = numbers.filter((_, i) => i % 2 === 1)
  const ordered = {
    ascending: numbers,
    descending: [ ...numbers ].reverse(),
    centre_odd_left: [...[ ...odds ].reverse(), ...evens],
    centre_even_left: [...[ ...evens ].reverse(), ...odds],
  }[pattern]

  return ordered.map(n => String(n).padStart(digits, '0'))
}

export function useSeatEditor(grid: SectionGrid) {
  const baseline: Ref<GridRow[]> = ref(clone(grid.rows))
  const rows: Ref<GridRow[]> = ref(clone(grid.rows))

  const changes = computed<Change[]>(() => {
    const draft = rows.value
    const base = baseline.value
    const result: Change[] = []
    const baseRows = new Map(base.map(row => [row.id, row]))
    const draftRows = new Map(draft.map(row => [row.id, row]))
    const baseSeats = new Map(base.flatMap(row => row.seats.map(seat => [seat.id, { row, seat } ] as const)))
    const draftSeats = new Map(draft.flatMap(row => row.seats.map(seat => [seat.id, { row, seat } ] as const)))

    draft.forEach((row) => {
      const before = baseRows.get(row.id)
      if (!before) {
        result.push({ kind: 'create', text: `Fila ${row.symbol} (linha ${row.order})` })
        return
      }
      if (before.symbol !== row.symbol) result.push({ kind: 'update', text: `Fila ${before.symbol} → ${row.symbol}` })
      if (before.order !== row.order) result.push({ kind: 'update', text: `Fila ${row.symbol}: linha ${before.order} → ${row.order}` })
    })
    base.forEach((row) => {
      if (draftRows.has(row.id)) return
      const seats = row.seats.length === 1 ? 'sua cadeira' : `suas ${row.seats.length} cadeiras`
      result.push({ kind: 'remove', text: `Fila ${row.symbol} e ${seats}` })
    })

    draftSeats.forEach(({ row, seat }, id) => {
      const before = baseSeats.get(id)
      if (!before) {
        const type = seat.type === SeatType.STANDARD ? '' : `, ${SeatTypeLabels[seat.type]}`
        result.push({ kind: 'create', text: `${fullLabel(row, seat)} (coluna ${seat.position}${type})` })
        return
      }

      const fields: string[] = []
      if (before.row.id !== row.id) fields.push(`fila ${before.row.symbol} → ${row.symbol}`)
      if (before.seat.name !== seat.name) fields.push(`nome ${before.seat.name} → ${seat.name}`)
      if (before.seat.label !== seat.label) fields.push(`rótulo "${before.seat.label ?? ''}" → "${seat.label ?? ''}"`)
      if (before.seat.position !== seat.position) fields.push(`coluna ${before.seat.position} → ${seat.position}`)
      if (before.seat.type !== seat.type) fields.push(`${SeatTypeLabels[before.seat.type]} → ${SeatTypeLabels[seat.type]}`)
      if (fields.length) result.push({ kind: 'update', text: `${fullLabel(row, seat)}: ${fields.join(', ')}` })
    })
    baseSeats.forEach(({ row, seat }, id) => {
      // A removed Row's seats are counted with it
      if (!draftSeats.has(id) && draftRows.has(row.id)) result.push({ kind: 'remove', text: fullLabel(row, seat) })
    })

    return result
  })

  /** Saved Rows and Seats whose symbol or name changed: a CSV re-import still using the old one recreates them */
  const renamed = computed<string[]>(() => {
    const baseRows = new Map(baseline.value.map(row => [row.id, row]))
    const baseSeats = new Map(baseline.value.flatMap(row => row.seats.map(seat => [seat.id, { row, seat } ] as const)))

    return [
      ...rows.value.flatMap((row) => {
        const before = baseRows.get(row.id)
        return before && before.symbol !== row.symbol ? [ `Fila ${before.symbol} → ${row.symbol}` ] : []
      }),
      ...rows.value.flatMap(row => row.seats.flatMap((seat) => {
        const before = baseSeats.get(seat.id)
        return before && before.seat.name !== seat.name ? [ `${before.row.symbol}${before.seat.name} → ${row.symbol}${seat.name}` ] : []
      })),
    ]
  })

  // Undo ------------------------------------------------------------------------------------------------------------

  const history: Ref<HistoryEntry[]> = ref([])
  let depth = 0

  /**
   * Runs `fn` as one undoable step. A step that returns an error is rolled back; steps run inside another one (a
   * paint stroke, a bulk action) become part of it.
   */
  function step(label: string, fn: () => Result): Result {
    const before = JSON.stringify(rows.value)
    depth++

    let result: Result
    try {
      result = fn()
    } finally {
      depth--
    }

    if (result !== null) {
      rows.value = JSON.parse(before) as GridRow[]
    } else if (depth === 0 && JSON.stringify(rows.value) !== before) {
      history.value.push({ label, before })
    }

    return result
  }

  function undo(): HistoryEntry | null {
    const entry = history.value.pop()
    if (!entry) return null

    rows.value = JSON.parse(entry.before) as GridRow[]
    return entry
  }

  /** " na fila A" for one Row, "" for the whole Section */
  function where(rowId?: string): string {
    return rowId ? ` na fila ${rowById(rowId)?.symbol ?? ''}` : ''
  }

  /** The grid the API answered a save with (new rows and seats now have ids) becomes the starting point */
  function markSaved(saved: SectionGrid) {
    baseline.value = clone(saved.rows)
    rows.value = clone(saved.rows)
    history.value = []
  }

  function discard() {
    rows.value = clone(baseline.value)
    history.value = []
  }

  // Lookups ---------------------------------------------------------------------------------------------------------

  function rowById(id: string): GridRow | undefined {
    return rows.value.find(row => row.id === id)
  }

  function seatAt(row: GridRow, position: number): GridSeat | undefined {
    return row.seats.find(seat => seat.position === position)
  }

  function sortSeats(row: GridRow) {
    row.seats.sort((a, b) => a.position - b.position)
  }

  function newSeat(name: string, position: number): GridSeat {
    return { id: `${NEW}${v4()}`, name, label: null, position, type: SeatType.STANDARD }
  }

  function seatById(id: string): PlacedSeat | undefined {
    for (const row of rows.value) {
      const seat = row.seats.find(s => s.id === id)
      if (seat) return { row, seat }
    }
    return undefined
  }

  /** The seats with the given ids that still exist, each once, with their Rows */
  function seatsById(ids: string[]): PlacedSeat[] {
    return [ ...new Set(ids) ].flatMap(id => seatById(id) ?? [])
  }

  function nameTaken(row: GridRow, name: string, exceptId?: string): boolean {
    return row.seats.some(seat => seat.name === name && seat.id !== exceptId)
  }

  /** The next free number in the Row, zero-padded like the Row's names (`digits` for an empty Row) */
  function suggestName(rowId: string, digits: number): string {
    const row = rowById(rowId)
    if (!row) return ''

    const width = rowDigits(row, digits)
    const numbers = row.seats.map(seat => Number.parseInt(seat.name, 10)).filter(n => !Number.isNaN(n))
    let next = numbers.length ? Math.max(...numbers) + 1 : 1
    while (nameTaken(row, String(next).padStart(width, '0'))) next++

    return String(next).padStart(width, '0')
  }

  // Seats -----------------------------------------------------------------------------------------------------------

  /**
   * Names the cells from column `from` on, one per name: a seat there keeps its id, Type and Label and only gets the
   * new name (it is the same physical chair); an empty cell gets a new seat.
   */
  function numberCells(rowId: string, from: number, names: string[]): Result {
    const row = rowById(rowId)
    if (!row) return 'A fila não existe mais.'

    const to = from + names.length - 1
    const outside = row.seats.filter(seat => seat.position < from || seat.position > to)
    const clash = outside.find(seat => names.includes(seat.name))
    if (clash) return `A fila ${row.symbol} já tem a cadeira ${clash.name} na coluna ${clash.position}, fora do trecho numerado.`

    names.forEach((name, i) => {
      const seat = seatAt(row, from + i)
      if (seat) seat.name = name
      else row.seats.push(newSeat(name, from + i))
    })
    sortSeats(row)
    return null
  }

  function addSeat(rowId: string, position: number, fields: Partial<SeatFields> = {}): Result {
    const row = rowById(rowId)
    if (!row) return 'A fila não existe mais.'

    const taken = seatAt(row, position)
    if (taken) return `A coluna ${position} da fila ${row.symbol} já tem a cadeira ${fullLabel(row, taken)}.`

    const name = (fields.name ?? suggestName(rowId, 1)).trim()
    if (!name) return 'A cadeira precisa de um nome.'
    if (nameTaken(row, name)) return `A fila ${row.symbol} já tem a cadeira ${name}.`

    row.seats.push({ ...newSeat(name, position), label: textOrNull(fields.label), type: fields.type ?? SeatType.STANDARD })
    sortSeats(row)
    return null
  }

  /** Changes one seat; moving it to a column that has another seat swaps the two */
  function updateSeat(seatId: string, patch: Partial<Omit<GridSeat, 'id'>>): Result {
    const found = seatById(seatId)
    if (!found) return 'A cadeira não existe mais.'
    const { row, seat } = found

    if (patch.name !== undefined) {
      const name = patch.name.trim()
      if (!name) return 'A cadeira precisa de um nome.'
      if (nameTaken(row, name, seat.id)) return `A fila ${row.symbol} já tem a cadeira ${name}.`
      seat.name = name
    }

    if (patch.label !== undefined) seat.label = textOrNull(patch.label)
    if (patch.type !== undefined) seat.type = patch.type

    if (patch.position !== undefined && patch.position !== seat.position) {
      if (!Number.isInteger(patch.position) || patch.position < 1) return 'A coluna deve ser um número a partir de 1.'

      const other = seatAt(row, patch.position)
      if (other) other.position = seat.position
      seat.position = patch.position
      sortSeats(row)
    }

    return null
  }

  function setType(seatIds: string[], type: SeatType): Result {
    seatsById(seatIds).forEach(({ seat }) => {
      seat.type = type
    })
    return null
  }

  function removeSeats(seatIds: string[]): Result {
    const ids = new Set(seatIds)
    rows.value.forEach((row) => {
      row.seats = row.seats.filter(seat => !ids.has(seat.id))
    })
    return null
  }

  /** Moves seats `delta` columns within their own Rows, all or none */
  function shiftSeats(seatIds: string[], delta: number): Result {
    const moving = seatsById(seatIds)
    const ids = new Set(moving.map(({ seat }) => seat.id))

    for (const { row, seat } of moving) {
      const target = seat.position + delta
      if (target < 1) return `${fullLabel(row, seat)} iria para antes da coluna 1.`

      const occupant = seatAt(row, target)
      if (occupant && !ids.has(occupant.id)) {
        return `A coluna ${target} da fila ${row.symbol} tem a cadeira ${fullLabel(row, occupant)}, que não está selecionada.`
      }
    }

    moving.forEach(({ seat }) => {
      seat.position += delta
    })
    moving.forEach(({ row }) => sortSeats(row))
    return null
  }

  /** "A01, A02" for a few seats, "12 cadeiras" for many */
  function describeSeats(seatIds: string[]): string {
    const found = seatsById(seatIds)
    return found.length > 4 ? plural(found.length, 'cadeira', 'cadeiras') : found.map(({ row, seat }) => fullLabel(row, seat)).join(', ')
  }

  function seatLabel(seatId: string): string {
    const found = seatById(seatId)
    return found ? fullLabel(found.row, found.seat) : ''
  }

  /** Renames seats in place, all at once so they can trade names; a name another seat of the Row keeps is refused */
  function renameSeats(renames: { seatId: string, name: string }[]): Result {
    const planned = renames.flatMap(({ seatId, name }) => {
      const found = seatById(seatId)
      return found ? [ { ...found, name: name.trim() } ] : []
    })
    const renamedIds = new Set(planned.map(({ seat }) => seat.id))

    for (const [index, { row, name } ] of planned.entries()) {
      if (!name) return 'A cadeira precisa de um nome.'
      if (planned.some((other, i) => i < index && other.row.id === row.id && other.name === name)) {
        return `A cadeira ${row.symbol}${name} aparece duas vezes.`
      }

      const keeper = row.seats.find(seat => seat.name === name && !renamedIds.has(seat.id))
      if (keeper) return `A fila ${row.symbol} já tem a cadeira ${name} na coluna ${keeper.position}, fora da seleção.`
    }

    planned.forEach(({ seat, name }) => {
      seat.name = name
    })
    return null
  }

  // Rows ------------------------------------------------------------------------------------------------------------

  /** Front to back from 1, the API's Row order */
  function renumberRows() {
    rows.value.forEach((row, index) => {
      row.order = index + 1
    })
  }

  function suggestSymbol(): string {
    const symbols = new Set(rows.value.map(row => row.symbol))
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')

    return [...letters, ...letters.map(letter => `Z${letter}`)].find(symbol => !symbols.has(symbol)) ?? `F${rows.value.length + 1}`
  }

  /** The symbol as the API keeps it, or an error */
  function checkSymbol(symbol: string, exceptId?: string): { symbol: string } | { error: string } {
    const value = symbol.trim().toUpperCase()
    if (!value) return { error: 'A fila precisa de um identificador.' }
    if (value.length > 5) return { error: 'O identificador da fila tem no máximo 5 caracteres.' }
    if (rows.value.some(row => row.symbol === value && row.id !== exceptId)) return { error: `Já existe a fila ${value} nesta seção.` }

    return { symbol: value }
  }

  /** Adds an empty Row at `index`, 0 being the front (closest to the stage) */
  function addRow(index: number, symbol?: string): Result {
    const checked = checkSymbol(symbol ?? suggestSymbol())
    if ('error' in checked) return checked.error

    rows.value.splice(index, 0, { id: `${NEW}${v4()}`, symbol: checked.symbol, order: 0, seats: [] })
    renumberRows()
    return null
  }

  function renameRow(rowId: string, symbol: string): Result {
    const row = rowById(rowId)
    if (!row) return 'A fila não existe mais.'

    const checked = checkSymbol(symbol, rowId)
    if ('error' in checked) return checked.error

    row.symbol = checked.symbol
    return null
  }

  /** Moves a Row `delta` lines towards the back (positive) or the front (negative) */
  function moveRow(rowId: string, delta: number): Result {
    const from = rows.value.findIndex(row => row.id === rowId)
    const to = Math.min(rows.value.length - 1, Math.max(0, from + delta))
    if (from < 0 || from === to) return null

    rows.value.splice(to, 0, rows.value.splice(from, 1)[0])
    renumberRows()
    return null
  }

  function removeRow(rowId: string): Result {
    rows.value = rows.value.filter(row => row.id !== rowId)
    renumberRows()
    return null
  }

  // Aisles ----------------------------------------------------------------------------------------------------------

  /** The Rows an aisle goes through: one Row, or the whole Section */
  function aisleRows(rowId?: string): GridRow[] {
    return rowId ? rows.value.filter(row => row.id === rowId) : rows.value
  }

  /** Opens an empty column at `position`, pushing the seats from there one column to the right */
  function insertGap(position: number, rowId?: string): Result {
    aisleRows(rowId).forEach(row => row.seats.forEach((seat) => {
      if (seat.position >= position) seat.position++
    }))
    return null
  }

  /** Closes an empty column, pulling the seats after it one column to the left */
  function removeGap(position: number, rowId?: string): Result {
    const target = aisleRows(rowId)
    if (target.some(row => seatAt(row, position))) {
      return `A coluna ${position} não está vazia${rowId ? ` na fila ${target[0].symbol}` : ''}.`
    }

    target.forEach(row => row.seats.forEach((seat) => {
      if (seat.position > position) seat.position--
    }))
    return null
  }

  /** What the API receives: the whole grid, with ids only for the rows and seats it already has */
  function payload(): SectionGridPayload {
    const id = (value: string) => (isNew(value) ? {} : { id: value })

    return {
      rows: rows.value.map(row => ({
        ...id(row.id),
        symbol: row.symbol,
        order: row.order,
        seats: row.seats.map(seat => ({
          ...id(seat.id),
          name: seat.name,
          label: textOrNull(seat.label),
          position: seat.position,
          type: seat.type,
        })),
      })),
    }
  }

  return reactive({
    rows,
    changes,
    pendingCount: computed<number>(() => changes.value.length),
    renamed,
    history,
    undo,
    markSaved,
    discard,
    /** Runs several changes as one step (a paint stroke); returning an error rolls them all back */
    batch: (label: string, fn: () => Result | void) => step(label, () => fn() ?? null),
    suggestName,
    addSeat: (rowId: string, position: number, fields?: Partial<SeatFields>) => step(
      `Adicionar cadeira na fila ${rowById(rowId)?.symbol ?? ''}, coluna ${position}`,
      () => addSeat(rowId, position, fields),
    ),
    updateSeat: (seatId: string, patch: Partial<Omit<GridSeat, 'id'>>) => step(
      `Editar ${seatLabel(seatId)}`,
      () => updateSeat(seatId, patch),
    ),
    setType: (seatIds: string[], type: SeatType) => step(
      `${SeatTypeLabels[type]} em ${plural(seatsById(seatIds).length, 'cadeira', 'cadeiras')}`,
      () => setType(seatIds, type),
    ),
    removeSeats: (seatIds: string[]) => step(`Remover ${describeSeats(seatIds)}`, () => removeSeats(seatIds)),
    shiftSeats: (seatIds: string[], delta: number) => step(
      `Mover ${plural(seatsById(seatIds).length, 'cadeira', 'cadeiras')} ${plural(Math.abs(delta), 'coluna', 'colunas')} para a ${delta > 0 ? 'direita' : 'esquerda'}`,
      () => shiftSeats(seatIds, delta),
    ),
    renameSeats: (renames: { seatId: string, name: string }[]) => step(
      `Renomear ${plural(renames.length, 'cadeira', 'cadeiras')}`,
      () => renameSeats(renames),
    ),
    numberCells: (rowId: string, from: number, names: string[]) => step(
      `Numerar fila ${rowById(rowId)?.symbol ?? ''}, colunas ${from}–${from + names.length - 1}`,
      () => numberCells(rowId, from, names),
    ),
    addRow: (index: number, symbol?: string) => step(`Adicionar fila na linha ${index + 1}`, () => addRow(index, symbol)),
    renameRow: (rowId: string, symbol: string) => step(
      `Fila ${rowById(rowId)?.symbol ?? ''} → ${symbol.trim().toUpperCase()}`,
      () => renameRow(rowId, symbol),
    ),
    moveRow: (rowId: string, delta: number) => step(
      `Mover fila ${rowById(rowId)?.symbol ?? ''} para ${delta < 0 ? 'a frente' : 'trás'}`,
      () => moveRow(rowId, delta),
    ),
    removeRow: (rowId: string) => step(`Remover fila ${rowById(rowId)?.symbol ?? ''}`, () => removeRow(rowId)),
    insertGap: (position: number, rowId?: string) => step(
      `Abrir corredor na coluna ${position}${where(rowId)}`,
      () => insertGap(position, rowId),
    ),
    removeGap: (position: number, rowId?: string) => step(
      `Fechar a coluna ${position}${where(rowId)}`,
      () => removeGap(position, rowId),
    ),
    payload,
  })
}

export type SeatEditor = ReturnType<typeof useSeatEditor>
